import { BoxIcon } from 'components/Icon/icons/box'
import {
  ORDER_MODEL_KEY,
  ORDER_MODEL_STATUS,
  ORDER_MODEL_STATUS_COLOR,
  ORDER_MODEL_STATUS_LABEL,
  ORDER_MODEL_STATUSES_CANCELLABLE,
  ORDER_MODEL_TRACKING_URL
} from 'models/daos/order/constants/index.cjs'
import { getDomainMapValueReduxUtil } from 'modules/redux/utils/get-domain-map-value'
import getValueReduxUtil from 'modules/redux/utils/get-value'
import getInstancesFromDomainMapReduxUtil from 'modules/redux/utils/legacy/get-instances-from-domain-map'
import getIdReduxUtil from 'modules/redux/utils/get-id'
import {
  capitalizeStringUtil,
  getObjectUtil
} from 'packages/src'

/**
 * Gets the shipping address of the given order.
 * @param  {Order} order
 * @param  {DomainInstance} address
 * @returns {Address}
 */
function getAddressOrderUtil (order, address) {
  if (!order) {
    return null
  }
  // Return the shipping address
  const addressId = getObjectUtil(order, 'data.address.data.id')
  return address.data[addressId]
}

/**
 * Gets the OrderProducts of the given order.
 * @param  {Order} order
 * @param  {Domain} orderProducts
 * @returns {OrderProducts[]}
 */
function getOrderProductsOrderUtil (order, orderProducts) {
  if (!order) {
    return []
  }
  const orderProductPointers = getValueReduxUtil(order, ORDER_MODEL_KEY.ORDER_PRODUCTS) || []
  const mapValues = orderProductPointers.map(getIdReduxUtil)
  return getInstancesFromDomainMapReduxUtil(orderProducts.data, mapValues)
}

/**
 * Gets the `PromoCodes` of the given order.
 * @param  {Order} order
 * @param  {DomainMap} promoCodes
 * @returns {PromoCode[]}
 */
function getPromoCodesOrderUtil (order, promoCodes) {
  if (!order) {
    return []
  }
  const promoCodePointers = getValueReduxUtil(order, ORDER_MODEL_KEY.PROMO_CODES) || []
  const values = promoCodePointers.map(getDomainMapValueReduxUtil)
  return getInstancesFromDomainMapReduxUtil(promoCodes.data, values)
}

/**
 * Gets the status label of the given status.
 * @param  {Order} order
 * @returns {string}
 */
function getStatusLabelOrderUtil (order) {
  const status = getValueReduxUtil(order, ORDER_MODEL_KEY.STATUS)
  let label = capitalizeStringUtil(ORDER_MODEL_STATUS_LABEL[status])
  // If the status failed, specify the reason
  if (status === ORDER_MODEL_STATUS.FAILED) {
    label += ` (${getValueReduxUtil(order, ORDER_MODEL_KEY.ERROR)})`
  }
  return label
}

/**
 * Gets the status label of the given status.
 * @param  {string} status
 * @returns {string}
 */
function getStatusColorOrderUtil (status) {
  return ORDER_MODEL_STATUS_COLOR[status]
}

/**
 * Gets the displayable text content.
 * @param  {Notification} notification
 * @param  {Instance[]} associatedInstances
 * @returns {Object}
 */
function getNotificationOrderUtil (notification, associatedInstances) {
  const orders = notification.data.orders
  const orderPointer = orders[0]
  const order = associatedInstances.find(
    a => a.data.id === orderPointer.data.id
  )
  const {
    key,
    status
  } = order.data
  const icon = BoxIcon
  const textPrefix = 'Your'
  const textBold = `order #${key}`
  let text
  if (status === ORDER_MODEL_STATUS.PENDING) {
    text = 'is'
  } else if (status === ORDER_MODEL_STATUS.PREPARATION) {
    text = 'is in'
  } else if (status === ORDER_MODEL_STATUS.FAILED) {
    text = ''
  } else {
    text = 'was'
  }
  text += ` ${ORDER_MODEL_STATUS_LABEL[status]}`
  const textUrl = `/orders/${key}`
  const imageUrl = `/orders/${key}`
  const iconColor = ORDER_MODEL_STATUS_COLOR[status]
  return {
    icon,
    iconColor,
    imageUrl,
    text,
    textBold,
    textPrefix,
    textUrl
  }
}

/**
 * Gets the url used to view the tracking.
 * @param  {Object} order
 * @returns {String}
 */
function getTrackingUrlOrderUtil (order) {
  const tracking = getValueReduxUtil(order, 'tracking')
  if (!tracking) {
    return
  }
  return ORDER_MODEL_TRACKING_URL + tracking
}

/**
 * Checks whether the order is cancellable.
 * @param  {Order|null}  order
 * @returns {boolean}
 */
function isCancellableOrderUtil (order) {
  return ORDER_MODEL_STATUSES_CANCELLABLE.includes(
    getObjectUtil(order, 'data.status')
  )
}

/**
 * Checks whether the order has payment failed.
 * @param  {Order|null}  order
 * @returns {boolean}
 */
function hasFailedOrderUtil (order) {
  return getObjectUtil(order, 'data.status') === ORDER_MODEL_STATUS.FAILED
}

export {
  getAddressOrderUtil,
  getOrderProductsOrderUtil,
  getPromoCodesOrderUtil,
  getStatusLabelOrderUtil,
  getStatusColorOrderUtil,
  getNotificationOrderUtil,
  getTrackingUrlOrderUtil,
  isCancellableOrderUtil,
  hasFailedOrderUtil
}
